import { Edit01 } from "@untitledui/icons";
import mapboxgl from "mapbox-gl";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Badge } from "@/components/ui/badges/badges";
import { Button } from "@/components/ui/buttons/button";
import { cx } from "@/utils/cx";

import { getSensorDurationLabel } from "../../utils/sensor/sensor-duration";
import { sensorStatusConfig } from "../../utils/sensor/sensor-status";
import { toTitleCase } from "../../utils/shared/strings";
import { isSensorMarkerFeature } from "./create-marker-feature";
import {
	getMapSensorMarkerPopupEmphasisLiftPx,
	getMapSensorMarkerPopupOffset,
} from "./map-marker.constants";

const POPUP_EMPHASIS_CLASS = "is-emphasized";

/**
 * Lift the open card with the pin scale.
 * Prefer Mapbox addClassName/removeClassName so emphasis survives popup rebuilds.
 */
const applyPopupEmphasis = (
	popup: mapboxgl.Popup | null,
	emphasized: boolean,
): boolean => {
	if (!popup) return false;

	popup.setOffset(getMapSensorMarkerPopupOffset());

	if (emphasized) {
		popup.addClassName(POPUP_EMPHASIS_CLASS);
	} else {
		popup.removeClassName(POPUP_EMPHASIS_CLASS);
	}

	const element = popup.getElement();
	if (!element) return false;

	element.style.setProperty(
		"--map-popup-emphasis-lift",
		emphasized ? `${getMapSensorMarkerPopupEmphasisLiftPx()}px` : "0px",
	);
	return true;
};

interface MapMarkerPopupProps {
	activeFeature: GeoJSON.Feature;
	focusAfterOpen?: boolean;
	isEmphasized?: boolean;
	map: mapboxgl.Map;
	onClose: () => void;
	onEdit?: (feature: GeoJSON.Feature) => void;
	/** Ignored — Shadow map config has no latest-event navigation. */
	showLatestEventLink?: boolean;
}

/**
 * Sensor pin popup for Map Configuration. Matches dashboard sensor info rows
 * and popup chrome (minus Latest Event); Edit stays as an inline action.
 */
export const MapMarkerPopup = ({
	activeFeature,
	focusAfterOpen = true,
	isEmphasized = false,
	map,
	onClose,
	onEdit,
}: MapMarkerPopupProps) => {
	const [containerElement] = useState(() => document.createElement("div"));
	const popupRef = useRef<mapboxgl.Popup | null>(null);
	const isEmphasizedRef = useRef(isEmphasized);
	const onCloseRef = useRef(onClose);

	const sensorId =
		typeof activeFeature.properties?.id === "string"
			? activeFeature.properties.id
			: typeof activeFeature.id === "string"
				? activeFeature.id
				: undefined;
	const name =
		typeof activeFeature.properties?.name === "string"
			? activeFeature.properties.name
			: "Sensor";
	const status =
		typeof activeFeature.properties?.status === "string"
			? activeFeature.properties.status
			: undefined;
	const type =
		typeof activeFeature.properties?.type === "string"
			? activeFeature.properties.type
			: undefined;
	const lastInputReceived = activeFeature.properties?.lastInputReceived as
		| Date
		| null
		| string
		| undefined;

	useLayoutEffect(() => {
		isEmphasizedRef.current = isEmphasized;
	}, [isEmphasized]);

	useLayoutEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	useEffect(() => {
		const popup = new mapboxgl.Popup({
			anchor: "bottom",
			className: cx(
				"map-custom-popup",
				isEmphasizedRef.current && POPUP_EMPHASIS_CLASS,
			),
			closeOnClick: false,
			focusAfterOpen,
			maxWidth: "320px",
			offset: getMapSensorMarkerPopupOffset(),
		})
			.setDOMContent(containerElement)
			.addTo(map);

		popupRef.current = popup;

		const handleClose = () => {
			// Map DOM can be removed during commit before React effect cleanup;
			// mapbox still emits "close" while the listener is attached.
			let mapGone: boolean;
			try {
				mapGone = !map.getContainer()?.isConnected;
			} catch {
				mapGone = true;
			}
			if (mapGone) return;
			onCloseRef.current();
		};
		popup.on("close", handleClose);

		applyPopupEmphasis(popup, isEmphasizedRef.current);

		return () => {
			popup.off("close", handleClose);
			// `popup.remove()` emits "close"; don't notify the parent during
			// intentional teardown (e.g. leaving map view), or URL sync can race.
			onCloseRef.current = () => {};
			popup.remove();
			popupRef.current = null;
		};
	}, [containerElement, focusAfterOpen, map]);

	useEffect(() => {
		const coordinates = activeFeature.geometry;
		if (coordinates.type !== "Point") return;
		popupRef.current?.setLngLat(coordinates.coordinates as [number, number]);
	}, [activeFeature]);

	useLayoutEffect(() => {
		if (applyPopupEmphasis(popupRef.current, isEmphasized)) return;
		const frame = requestAnimationFrame(() => {
			applyPopupEmphasis(popupRef.current, isEmphasized);
		});
		return () => cancelAnimationFrame(frame);
	}, [isEmphasized]);

	if (!isSensorMarkerFeature(activeFeature)) return null;

	const { color: statusBadgeColor, label: statusBadgeText } = status
		? sensorStatusConfig(status as Parameters<typeof sensorStatusConfig>[0])
		: { color: "gray" as const, label: "Unknown" };

	const durationLabel = getSensorDurationLabel(status, lastInputReceived);
	const displayName = toTitleCase(name);

	return createPortal(
		<div className="text-sm">
			<div className="mb-1 flex items-start gap-1">
				{onEdit ? (
					<Button
						aria-label={`Edit ${displayName}`}
						className="min-w-0 flex-1 justify-start text-left font-semibold whitespace-normal text-primary"
						color="link-color"
						onClick={() => onEdit(activeFeature)}
						size="sm"
					>
						<span className="line-clamp-2">
							{displayName}
							<Edit01
								aria-hidden
								className="ml-1 inline size-3 shrink-0 stroke-[2px] align-baseline text-brand-secondary group-hover:text-brand-secondary_hover"
							/>
						</span>
					</Button>
				) : (
					<p className="min-w-0 flex-1 font-semibold text-primary">
						{displayName}
					</p>
				)}
			</div>

			<ul className="flex flex-col gap-1 text-secondary">
				<li className="flex items-center gap-1 capitalize">
					<span>Sensor Type:</span>
					<span>{type ? toTitleCase(type) : "—"}</span>
				</li>
				<li className="flex items-center gap-1">
					<span>Sensor ID:</span>
					<span className="truncate">{sensorId?.slice(-12) ?? "—"}</span>
				</li>
				<li className="flex items-center gap-1">
					<span>Status: </span>
					<Badge color={statusBadgeColor} size="sm" type="color">
						{statusBadgeText}
					</Badge>
				</li>
			</ul>

			{durationLabel ? (
				<p className={cx("mt-1.5 font-medium", durationLabel.color)}>
					{durationLabel.text}
				</p>
			) : null}
		</div>,
		containerElement,
	);
};
